import { sanitizeArtifacts } from "./normalizedExecution";

const TERMINAL_OUTCOMES = new Set(["completed", "failed", "stopped"]);

export function createInitialObjectiveOutcomeState(conversationId = null) {
  return {
    conversationId,
    objective: null,
    outcome: null,
  };
}

function normalizeText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function createPersistedObjective(messages = [], now = Date.now()) {
  const message = [...messages].reverse().find((item) => item?.role === "user");
  const raw = message?.content;
  const text = normalizeText(
    typeof raw === "string" ? raw : raw?.text || raw?.content || "",
  );
  if (!text) return null;
  return {
    id: message.id || `persisted-${message.timestamp || now}`,
    text,
    source: "persisted_user_command",
    status: "active",
    createdAt: message.timestamp || now,
    updatedAt: now,
  };
}

export function objectiveOutcomeReducer(state, action) {
  const now = action.now || Date.now();

  if (action.type === "objective/reset") {
    return createInitialObjectiveOutcomeState(action.conversationId ?? null);
  }

  if (action.type === "objective/hydrate") {
    return {
      conversationId: action.conversationId ?? state.conversationId,
      objective: action.objective || null,
      // A saved assistant message is not proof of a structured outcome.
      outcome: null,
    };
  }

  if (action.type === "objective/begin") {
    const text = normalizeText(action.text);
    if (!text) return state;
    return {
      conversationId: action.conversationId ?? state.conversationId,
      objective: {
        id: action.objectiveId || `${action.conversationId || "new"}-${now}`,
        text,
        source: "user_command",
        status: "active",
        createdAt: now,
        updatedAt: now,
      },
      outcome: null,
    };
  }

  if (action.type !== "objective/execution_terminal") return state;
  if (!state.objective || TERMINAL_OUTCOMES.has(state.outcome?.status)) return state;
  const execution = action.execution || {};
  if (!TERMINAL_OUTCOMES.has(execution.phase)) return state;

  const status = execution.phase;
  const summary = status === "completed"
    ? normalizeText(execution.result?.content)
    : null;
  const error = status === "failed"
    ? {
        message: execution.error?.message || "Execution failed",
        code: execution.error?.code || null,
      }
    : status === "stopped"
      ? { message: execution.label || "Execution stopped", code: null }
      : null;

  return {
    ...state,
    conversationId: execution.conversationId || state.conversationId,
    objective: {
      ...state.objective,
      status,
      updatedAt: execution.completedAt || now,
    },
    outcome: {
      status,
      summary: summary || null,
      provider: execution.result?.provider || null,
      completedAt: execution.completedAt || now,
      error,
      artifacts: sanitizeArtifacts(execution.artifacts),
    },
  };
}

export function selectObjectiveLabel(state, fallback = "Start a new command") {
  const text = normalizeText(state?.objective?.text);
  if (!text) return fallback;
  return text.length > 78 ? `${text.slice(0, 75)}...` : text;
}

export function selectObjectiveOutcomePresentation(state) {
  const objective = state?.objective;
  const outcome = state?.outcome;
  return {
    objectiveData: objective
      ? {
          id: objective.id || null,
          title: objective.text,
          status: objective.status,
        }
      : null,
    outcome: outcome
      ? {
          status: outcome.status,
          summary: outcome.summary,
          provider: outcome.provider,
          completedAt: outcome.completedAt,
          error: outcome.error,
          artifacts: sanitizeArtifacts(outcome.artifacts),
          // Reuse actions arrive in a later, independently gated phase.
          actions: [],
        }
      : null,
  };
}
